import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import PresentationSlide from './components/PresentationSlide'
import EnhancedNavigation from './components/EnhancedNavigation'
import EnhancedSlideIndicator from './components/EnhancedSlideIndicator'
import ParticleBackground from './components/ParticleBackground'
import Navigation from './components/Navigation'
import Footer from './components/Footer'
import { useSwipe } from './hooks/useSwipe'
import { slides } from './data/slides'
import HomePage from './pages/HomePage'
import SlideGenerator from './pages/SlideGenerator'
import Presentation from './pages/Presentation'
import BibleReader from './pages/BibleReader'
import About from './pages/About'
import Contact from './pages/Contact'

// Daniel Chapter 7 Presentation Component (existing)
function DanielChapter7() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault()
          setCurrentSlide((prev) => (prev + 1) % slides.length)
          break
        case 'ArrowLeft':
          e.preventDefault()
          setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
          break
        case 'f':
        case 'F11':
          e.preventDefault()
          toggleFullscreen()
          break
        case 'Escape':
          if (isFullscreen) {
            exitFullscreen()
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [isFullscreen])

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen()
      setIsFullscreen(true)
    } else {
      await document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  const exitFullscreen = async () => {
    await document.exitFullscreen()
    setIsFullscreen(false)
  }

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
  }

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
  }

  const goHome = () => {
    window.location.href = '/'
  }

  // Swipe functionality for mobile
  const swipeHandlers = useSwipe({
    onSwipeLeft: nextSlide,
    onSwipeRight: prevSlide,
    threshold: 50
  })

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"
      {...swipeHandlers}
    >
      <Navigation />
      <ParticleBackground />
      
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <PresentationSlide 
            slide={slides[currentSlide]} 
            slideNumber={currentSlide + 1}
            totalSlides={slides.length}
          />
        </motion.div>
      </AnimatePresence>

      {/* Enhanced Navigation System */}
      <EnhancedNavigation
        currentSlide={currentSlide}
        totalSlides={slides.length}
        onNext={nextSlide}
        onPrev={prevSlide}
        onGoToSlide={goToSlide}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        onGoHome={goHome}
      />

      <EnhancedSlideIndicator
        currentSlide={currentSlide}
        totalSlides={slides.length}
        onGoToSlide={goToSlide}
        slides={slides}
      />

      <Footer />
    </div>
  )
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/slideshow-generator" element={<SlideGenerator />} />
        <Route path="/presentation" element={<Presentation />} />
        <Route path="/book/:bookId" element={<BibleReader />} />
        <Route path="/book/:bookId/:chapter" element={<BibleReader />} />
        <Route path="/bible-studies/daniel/chapter7" element={<DanielChapter7 />} />
        <Route path="/bible-studies/daniel/chapter7/" element={<DanielChapter7 />} />
        {/* Redirect old routes */}
        <Route path="/daniel-chapter7" element={<Navigate to="/bible-studies/daniel/chapter7" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  )
}

export default App

